import { useEffect, useRef } from "react";
import { clearActiveChainId, isChainOverridden } from "@/config/activeChain";
import { useAuth } from "@/hooks/useAuth";

/**
 * Keeps the network override an ADMIN affordance rather than a browser one.
 *
 * The override lives in a single browser-global localStorage key that is read
 * at module load, before any role is known. Without this, an admin who picks
 * Mainnet and signs out leaves the next person on that browser running against
 * real funds -- with no confirmation (that only fires on the switch's own
 * click) and no way back (the switch is hidden for non-admins).
 *
 * So once the session resolves to a non-admin, drop the override and reload
 * onto the deployment's own chain. The reload is the same contract switchChain
 * uses: every module re-initialises together instead of leaving the page half
 * on one chain.
 */
export function useChainOverridePolicy(): void {
  const { isLoading, isAuthenticated, isAdmin, profileSynced } = useAuth();
  const enforced = useRef(false);

  useEffect(() => {
    // Wait for the role to actually be known. `isLoading` only tracks the
    // Privy SDK becoming ready; usePrivySync then publishes a session with the
    // placeholder role "user" and only fills in the real role once the profiles
    // edge function answers. Acting in that window cleared an admin's own
    // choice out from under them: picking Testnet reloaded, the reloaded page
    // saw isLoading=false + role "user", dropped the override and reloaded
    // straight back onto Mainnet, so the switch looked like it did nothing
    // (DEV-793). `profileSynced` is the store's "the role has been fetched"
    // signal -- the same one AuthGuard gates its spinner on -- and it is set
    // on both the success and the failure path, so a broken fetch still falls
    // back to the deployment's own chain rather than latching the override.
    // Waiting on it is only safe because the fetch is bounded: callEdgeFunction
    // gives the profiles call a 15s deadline, so it always reaches one of those
    // two paths. Without that a hung request would leave the role unknown --
    // and the override in place -- for the life of the page.
    if (isLoading || !isAuthenticated || !profileSynced) return;
    if (isAdmin || !isChainOverridden()) return;
    if (enforced.current) return;
    enforced.current = true;

    clearActiveChainId();
    window.location.reload();
  }, [isLoading, isAuthenticated, isAdmin, profileSynced]);
}
